import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Requests browser notification permission on mount and shows a notification
 * whenever a new inbound WhatsApp message arrives (source: twilio_divus or live).
 *
 * @param selectedContactId - the contact currently open in the chat view.
 *   If the new message belongs to this contact, the notification is suppressed.
 */
export function useBrowserNotificationsForWhatsApp(selectedContactId?: string | null) {
  const permissionRef = useRef<NotificationPermission>('default');

  // Request permission once on mount
  useEffect(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      permissionRef.current = 'granted';
      return;
    }
    if (Notification.permission !== 'denied') {
      Notification.requestPermission().then((perm) => {
        permissionRef.current = perm;
      });
    } else {
      permissionRef.current = 'denied';
    }
  }, []);

  // Realtime subscription — listen for new inbound WhatsApp messages
  useEffect(() => {
    const channel = supabase
      .channel('browser-notif-whatsapp')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: 'channel=eq.whatsapp',
        },
        (payload) => {
          const msg = payload.new as Record<string, unknown>;

          // Only inbound messages from the live channels
          if (msg.direction !== 'inbound') return;
          if (!['twilio_divus', 'live'].includes(msg.source as string)) return;

          // Suppress if the user is already looking at this contact's conversation
          if (selectedContactId && msg.contact_id === selectedContactId) return;

          if (!('Notification' in window)) return;
          if (Notification.permission !== 'granted') return;

          const senderName = (msg.contact_name as string) || 'Cliente';
          const body = (msg.content as string) || 'Nova mensagem de WhatsApp';

          new Notification(`WhatsApp — ${senderName}`, {
            body,
            icon: '/favicon.ico',
            tag: `whatsapp-${msg.contact_id}`,
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedContactId]);
}
